import React from 'react';

/**
 * Landing-system CTA band.
 *
 * Final-call CTA strip. Two tones:
 * - 'accent' (default): solid bg via --landing-accent + radial halo via
 *   --landing-accent-ring. White text. Used by Kloutforce-style pages.
 * - 'light': light slab on top of accent foreground (Kloutsale uses a
 *   sky-500 band with slate-950 text). Caller picks heading/cta colors via
 *   slots — we keep the surface neutral and explicit.
 *
 * Heading/description/cta are slots so each brand controls copy + buttons.
 */
export type LandingCTATone = 'accent' | 'light';

type Props = {
  tone?: LandingCTATone;
  heading: React.ReactNode;
  description?: React.ReactNode;
  cta: React.ReactNode;
  className?: string;
};

export default function LandingCTABand({
  tone = 'accent',
  heading,
  description,
  cta,
  className = '',
}: Props) {
  if (tone === 'light') {
    return (
      <section
        className={`border-t border-white/10 bg-[hsl(var(--landing-accent))] py-20 text-slate-950 md:py-24 ${className}`.trim()}
      >
        <div className="mx-auto max-w-5xl px-6 text-center">
          <h2 className="text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl">
            {heading}
          </h2>
          {description ? (
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-950/75">
              {description}
            </p>
          ) : null}
          <div className="mt-8 flex flex-wrap justify-center gap-4">{cta}</div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`relative overflow-hidden bg-[hsl(var(--landing-accent))] py-20 text-white md:py-24 ${className}`.trim()}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,hsl(var(--landing-accent-ring)/0.5),transparent_55%)]" />
      <div className="relative mx-auto max-w-3xl px-6 text-center">
        <h2 className="text-4xl font-semibold tracking-tight text-white md:text-5xl">{heading}</h2>
        {description ? (
          <p className="mx-auto mt-5 max-w-xl text-lg leading-8 text-white/85">{description}</p>
        ) : null}
        <div className="mt-10 flex justify-center">{cta}</div>
      </div>
    </section>
  );
}